import { FC, ReactNode } from 'react'

// Icônes SVG dédiées à ui-v2, distinctes du système Icomoon de components-v2/Icon
// (police d'icônes existante non modifiable ici). Tracés repris fidèlement de
// taste-creation-form-design/maquette.js (SVG_CHEV, SVG_LOCK, SVG_CHECK, ICONS.alert...).
export type UiIconName = 'chevron-right' | 'chevron-down' | 'check' | 'lock' | 'alert' | 'cross' | 'plus' | 'arrow-right' | 'building' | 'chart' | 'file' | 'shield' | 'briefcase' | 'sparkle' | 'upload'

type UiIconProps = {
  name: UiIconName
  size?: number
  className?: string
}

type IconDef = {
  paths: ReactNode
  strokeWidth: number
  strokeLinecap?: 'square' | 'round'
  strokeLinejoin?: 'round'
}

const ICONS: Record<UiIconName, IconDef> = {
  'chevron-right': { paths: <path d="M9 5l7 7-7 7" />, strokeWidth: 2, strokeLinecap: 'square' },
  'chevron-down': {
    paths: <path d="M9 5l7 7-7 7" transform="rotate(90 12 12)" />, strokeWidth: 2, strokeLinecap: 'square',
  },
  check: { paths: <path d="M5 13l4 4L19 7" />, strokeWidth: 2.5, strokeLinecap: 'square' },
  lock: {
    paths: (
      <>
        <rect x="5" y="11" width="14" height="9" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </>
    ),
    strokeWidth: 2,
  },
  alert: {
    paths: (
      <>
        <path d="M12 4l9 16H3z" />
        <path d="M12 10v4M12 17.5v.1" />
      </>
    ),
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
  cross: { paths: <path d="M6 6l12 12M18 6L6 18" />, strokeWidth: 1.8, strokeLinecap: 'round' },
  // Étoile 4 branches — remplace le glyphe texte « ✧ » des encarts conseil (`.tip .sparkle` de la
  // maquette), ajoutée le 2026-09-10 pour `ui-v2/Callout` variant="info".
  sparkle: {
    paths: <path d="M12 3l2.2 6.8L21 12l-6.8 2.2L12 21l-2.2-6.8L3 12l6.8-2.2z" />,
    strokeWidth: 1.6,
    strokeLinejoin: 'round',
  },
  plus: { paths: <path d="M12 5v14M5 12h14" />, strokeWidth: 1.8, strokeLinecap: 'round' },
  'arrow-right': { paths: <path d="M5 12h14M13 6l6 6-6 6" />, strokeWidth: 2, strokeLinecap: 'square' },
  building: {
    paths: (
      <>
        <rect x="6" y="4" width="12" height="16" rx="1" />
        <path d="M10 8h.5M13.5 8h.5M10 12h.5M13.5 12h.5M10 16h4" />
      </>
    ),
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
  chart: {
    paths: <path d="M5 19V10M11 19V5M17 19v-6M3 21h18" />,
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
  file: {
    paths: (
      <>
        <path d="M7 3h7l4 4v14H7z" />
        <path d="M14 3v4h4M10 12h5M10 16h5" />
      </>
    ),
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
  shield: {
    paths: (
      <>
        <path d="M12 3l7 3v5c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
  briefcase: {
    paths: (
      <>
        <rect x="4" y="8" width="16" height="11" rx="2" />
        <path d="M9 8V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M4 13h16" />
      </>
    ),
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
  // Flèche sortant d'un bac — ajoutée le 2026-09-14 pour la zone de dépôt de `Justifs/JustifRow`
  // (aucun équivalent dans les `ICONS` de la maquette).
  upload: {
    paths: (
      <>
        <path d="M12 16V5" />
        <path d="M7.5 9.5L12 5l4.5 4.5" />
        <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
      </>
    ),
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
}

const UiIcon: FC<UiIconProps> = ({ name, size = 16, className = '' }) => {
  const {
    paths, strokeWidth, strokeLinecap, strokeLinejoin,
  } = ICONS[name]

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap={strokeLinecap}
      strokeLinejoin={strokeLinejoin}
      className={className}
      aria-hidden="true"
    >
      {paths}
    </svg>
  )
}

export default UiIcon
